import React, { useState, useMemo, useEffect } from "react";
import { Table, Row, Col, Modal, Spinner } from "react-bootstrap";
import Footer from "../layouts/Footer";
import {
  fetchGsmGateways,
  fetchGsmLocations,
  addGsmGateway,
  updateGsmGateway,
  fetchGsmAssignments
} from "../services/api";
import { GsmStyles, GsmToasts, useNotifications, isValidIp, isValidHost } from "./GsmShared";

const EMPTY_GATEWAY = {
  internal_id: '',
  ports_count: '',
  local_ip: '',
  remote_ip: '',
  location: ''
};

const normalizeGateway = (g) => ({
  id: g.id,
  internal_id: g.internal_id || '',
  ports_count: Number(g.ports_count) || 0,
  local_ip: g.local_ip || '',
  remote_ip: g.remote_ip || '',
  location: g.location || '',
  created_at: g.created_at
});

export default function GsmPasserelles() {
  const [gateways, setGateways] = useState([]);
  const [locations, setLocations] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [filterText, setFilterText] = useState('');
  const [locationFilter, setLocationFilter] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_GATEWAY);
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const { notifications, showNotification, hideNotification } = useNotifications();

  // ============================================
  // CHARGEMENT
  // ============================================
  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async (showRefreshSpinner = false) => {
    try {
      if (showRefreshSpinner) setRefreshing(true);
      else setLoading(true);
      setError("");

      const token = localStorage.getItem('authToken');
      const [gatewaysData, assignmentsData, locationsData] = await Promise.all([
        fetchGsmGateways(token),
        fetchGsmAssignments(token),
        fetchGsmLocations(token)
      ]);

      setGateways(gatewaysData.map(normalizeGateway));
      setLocations(locationsData);
      setAssignments(assignmentsData);
      if (showRefreshSpinner) showNotification('Liste des passerelles actualisée', 'success', 3000);
    } catch (err) {
      setError(err.message || "Erreur lors du chargement des passerelles");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // ============================================
  // DONNÉES DÉRIVÉES
  // ============================================
  const usedPortsByGateway = useMemo(() => {
    const map = {};
    assignments.forEach(a => {
      const key = String(a.gateway_id);
      if (!map[key]) map[key] = [];
      map[key].push(Number(a.port_number));
    });
    return map;
  }, [assignments]);


  const filteredGateways = useMemo(() => {
    const search = filterText.trim().toLowerCase();
    return gateways.filter(g => {
      if (locationFilter && g.location !== locationFilter) return false;
      if (!search) return true;
      return [g.id, g.internal_id, g.local_ip, g.remote_ip, g.location]
        .some(v => String(v ?? '').toLowerCase().includes(search));
    });
  }, [gateways, filterText, locationFilter]);

  const stats = useMemo(() => {
    const totalPorts = gateways.reduce((sum, g) => sum + g.ports_count, 0);
    return {
      total: gateways.length,
      totalPorts,
      usedPorts: assignments.length,
      locations: locations.length
    };
  }, [gateways, assignments, locations]);

  // ============================================
  // AJOUT / MODIFICATION
  // ============================================
  const openAddModal = () => {
    setEditingId(null);
    setForm(EMPTY_GATEWAY);
    setShowModal(true);
  };

  const openEditModal = (gateway) => {
    setEditingId(gateway.id);
    setForm({
      internal_id: gateway.internal_id,
      ports_count: String(gateway.ports_count),
      local_ip: gateway.local_ip,
      remote_ip: gateway.remote_ip,
      location: gateway.location
    });
    setShowModal(true);
  };

  const maxUsedPort = editingId != null
    ? Math.max(0, ...(usedPortsByGateway[String(editingId)] || []))
    : 0;

  const formErrors = useMemo(() => {
    const errors = {};
    const ports = Number(form.ports_count);
    if (!form.internal_id.trim()) errors.internal_id = "L'ID interne est obligatoire";
    else if (gateways.some(g => g.internal_id.toLowerCase() === form.internal_id.trim().toLowerCase() && g.id !== editingId)) {
      errors.internal_id = "Cet ID interne est déjà utilisé";
    }
    if (!Number.isInteger(ports) || ports < 1) errors.ports_count = "Nombre de ports invalide";
    else if (ports < maxUsedPort) errors.ports_count = `Le port ${maxUsedPort} est réservé : minimum ${maxUsedPort} ports`;
    if (!isValidIp(form.local_ip)) errors.local_ip = "Adresse IPv4 invalide";
    if (!isValidHost(form.remote_ip)) errors.remote_ip = "Adresse IP ou nom d'hôte invalide";
    if (!form.location.trim()) errors.location = "La localisation est obligatoire";
    return errors;
  }, [form, gateways, editingId, maxUsedPort]);

  const isFormValid = Object.keys(formErrors).length === 0;

  const handleSave = async () => {
    setSubmitted(true);
    if (!isFormValid) return;

    const payload = {
      internal_id: form.internal_id.trim(),
      ports_count: Number(form.ports_count),
      local_ip: form.local_ip.trim(),
      remote_ip: form.remote_ip.trim(),
      location: form.location.trim()
    };

    try {
      setSaving(true);
      const token = localStorage.getItem('authToken');
      if (editingId != null) {
        await updateGsmGateway(token, editingId, payload);
        showNotification('Passerelle modifiée avec succès', 'success');
      } else {
        await addGsmGateway(token, payload);
        showNotification('Passerelle ajoutée avec succès', 'success');
      }
      setShowModal(false);
      setSubmitted(false);
      await loadData();
    } catch (err) {
      showNotification(err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const fieldError = (name) => submitted && formErrors[name];

  const renderField = (name, label, props = {}) => (
    <div>
      <label className="gsm-form-label">{label} <span className="gsm-required">*</span></label>
      <input
        className={`gsm-input ${fieldError(name) ? 'is-invalid' : ''}`}
        value={form[name]}
        onChange={(e) => setForm({ ...form, [name]: e.target.value })}
        {...props}
      />
      {fieldError(name) && <div className="gsm-form-error">{formErrors[name]}</div>}
    </div>
  );

  // ============================================
  // RENDER
  // ============================================
  return (
    <div className="gsm-page main main-app p-3 p-lg-4">
      <GsmStyles />
      <GsmToasts notifications={notifications} onClose={hideNotification} />

      {/* ========== HEADER ========== */}
      <div className="gsm-header mb-4">
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
          <div className="d-flex align-items-center gap-3">
            <div className="gsm-header-icon">
              <i className="ri-router-line"></i>
            </div>
            <div>
              <h3 className="gsm-header-title mb-1">Passerelles GSM</h3>
              <p className="gsm-header-subtitle mb-0">Inventaire et configuration des passerelles GSM</p>
            </div>
          </div>
          <div className="d-flex flex-wrap gap-2">
            <button className="gsm-btn-secondary" onClick={() => loadData(true)} disabled={refreshing}>
              <i className={`bi ${refreshing ? 'bi-arrow-repeat gsm-spin' : 'bi-arrow-clockwise'}`}></i>
              <span>Actualiser</span>
            </button>
            <button className="gsm-btn-primary" onClick={openAddModal}>
              <i className="bi bi-plus-lg"></i>
              <span>Ajouter une passerelle</span>
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="gsm-alert gsm-alert-danger mb-4">
          <i className="bi bi-exclamation-triangle-fill"></i>
          <div className="flex-grow-1">{error}</div>
          <button className="gsm-btn-secondary" onClick={() => loadData(true)}>Réessayer</button>
        </div>
      )}

      {/* ========== STATS ========== */}
      <Row className="g-3 mb-4">
        {[
          { label: 'Passerelles', value: stats.total, icon: 'ri-router-line', color: 'blue' },
          { label: 'Ports au total', value: stats.totalPorts, icon: 'ri-sim-card-2-line', color: 'violet' },
          { label: 'Ports réservés', value: stats.usedPorts, icon: 'ri-lock-line', color: 'amber' },
          { label: 'Localisations', value: stats.locations, icon: 'ri-map-pin-line', color: 'green' }
        ].map(s => (
          <Col xs={6} lg={3} key={s.label}>
            <div className="gsm-stat-card">
              <div className={`gsm-stat-icon gsm-icon-${s.color}`}><i className={s.icon}></i></div>
              <div>
                <div className="gsm-stat-label">{s.label}</div>
                <div className="gsm-stat-value">{s.value}</div>
              </div>
            </div>
          </Col>
        ))}
      </Row>

      {/* ========== FILTRES ========== */}
      <div className="gsm-filters mb-4">
        <Row className="g-3 align-items-center">
          <Col lg={8}>
            <div className="gsm-search-wrapper">
              <i className="bi bi-search gsm-search-icon"></i>
              <input
                type="text"
                className="gsm-search-input"
                placeholder="Rechercher par ID, ID interne, adresse IP, localisation..."
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
              />
            </div>
          </Col>
          <Col lg={4}>
            <select className="gsm-select" value={locationFilter} onChange={(e) => setLocationFilter(e.target.value)}>
              <option value="">Toutes les localisations</option>
              {locations.map(l => <option key={l} value={l}>{l}</option>)}
            </select>
          </Col>
        </Row>
      </div>

      {/* ========== TABLE ========== */}
      <div className="gsm-table-card mb-4">
        {loading ? (
          <div className="gsm-empty"><Spinner animation="border" /><div className="mt-2">Chargement des passerelles...</div></div>
        ) : filteredGateways.length === 0 ? (
          <div className="gsm-empty">
            <i className="ri-router-line"></i>
            {gateways.length === 0 ? 'Aucune passerelle enregistrée' : 'Aucune passerelle ne correspond à la recherche'}
          </div>
        ) : (
          <div className="table-responsive">
            <Table className="gsm-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>ID interne</th>
                  <th>Nombre de ports</th>
                  <th>IP locale</th>
                  <th>IP distante</th>
                  <th>Localisation</th>
                  <th>Occupation</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredGateways.map(g => {
                  const used = (usedPortsByGateway[String(g.id)] || []).length;
                  const pct = g.ports_count ? Math.round((used / g.ports_count) * 100) : 0;
                  return (
                    <tr key={g.id}>
                      <td><span className="gsm-mono">#{g.id}</span></td>
                      <td><span className="gsm-code">{g.internal_id}</span></td>
                      <td>{g.ports_count}</td>
                      <td><span className="gsm-mono">{g.local_ip}</span></td>
                      <td><span className="gsm-mono">{g.remote_ip}</span></td>
                      <td>
                        <span className="gsm-badge gsm-badge-gray">
                          <i className="ri-map-pin-line"></i>{g.location}
                        </span>
                      </td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <div className="gsm-progress"><div style={{ width: `${pct}%` }}></div></div>
                          <small className="text-muted">{used}/{g.ports_count}</small>
                        </div>
                      </td>
                      <td className="text-end">
                        <button className="gsm-action-btn gsm-action-edit" title="Modifier" onClick={() => openEditModal(g)}>
                          <i className="bi bi-pencil"></i>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        )}
      </div>

      {/* ========== MODAL AJOUT / ÉDITION ========== */}
      <Modal show={showModal} onHide={() => { setShowModal(false); setSubmitted(false); }} size="lg" centered className="gsm-modal">
        <div className={`gsm-modal-header ${editingId != null ? 'gsm-modal-header-blue' : 'gsm-modal-header-success'}`}>
          <div className="d-flex align-items-center gap-3">
            <div className="gsm-modal-icon"><i className="ri-router-line"></i></div>
            <div>
              <h5 className="gsm-modal-title">
                {editingId != null ? `Modifier la passerelle #${editingId}` : 'Nouvelle passerelle'}
              </h5>
              <small className="gsm-modal-subtitle">Paramètres réseau et emplacement</small>
            </div>
          </div>
          <button className="gsm-modal-close" onClick={() => { setShowModal(false); setSubmitted(false); }}>
            <i className="bi bi-x-lg"></i>
          </button>
        </div>

        <Modal.Body className="gsm-modal-body">
          <Row className="g-3">
            <Col md={6}>{renderField('internal_id', 'ID interne', { placeholder: 'GW-LBV-01' })}</Col>
            <Col md={6}>
              {renderField('ports_count', 'Nombre de ports', { type: 'number', min: 1, placeholder: '8' })}
              {maxUsedPort > 0 && !fieldError('ports_count') && (
                <div className="gsm-form-hint"><i className="bi bi-info-circle"></i>Port le plus élevé réservé : {maxUsedPort}</div>
              )}
            </Col>
            <Col md={6}>{renderField('local_ip', 'Adresse IP locale', { placeholder: '192.168.1.10' })}</Col>
            <Col md={6}>{renderField('remote_ip', 'Adresse IP distante', { placeholder: '41.158.10.20 ou hote.example.com' })}</Col>
            <Col md={12}>
              <label className="gsm-form-label">Localisation <span className="gsm-required">*</span></label>
              <select
                className={`gsm-select ${fieldError('location') ? 'is-invalid' : ''}`}
                value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
              >
                <option value="">Sélectionner une localisation</option>
                {/* Conserve la valeur existante si elle n'est plus dans la liste de référence */}
                {form.location && !locations.includes(form.location) && (
                  <option value={form.location}>{form.location}</option>
                )}
                {locations.map(l => <option key={l} value={l}>{l}</option>)}
              </select>
              {fieldError('location') && <div className="gsm-form-error">{formErrors.location}</div>}
              {locations.length === 0 && (
                <div className="gsm-form-hint"><i className="bi bi-info-circle"></i>Aucune localisation disponible via /gsm/locations</div>
              )}
            </Col>
          </Row>
        </Modal.Body>

        <div className="gsm-modal-footer">
          <button className="gsm-btn-cancel" onClick={() => { setShowModal(false); setSubmitted(false); }}>Annuler</button>
          <button className="gsm-btn-success" onClick={handleSave} disabled={saving}>
            {saving ? <><Spinner animation="border" size="sm" /> Enregistrement...</> : <><i className="bi bi-check-lg"></i> Enregistrer</>}
          </button>
        </div>
      </Modal>

      <Footer />
    </div>
  );
}
